import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import { SEARCH_DEBOUNCE_MS } from '../../../core/constants/forms';
import { IconChart, IconEdit, IconPlus, IconReceipt, IconUsers } from '../../../common/icons';
import LoadMore from '../../../common/components/LoadMore/LoadMore';
import { useAnimalsInfinite } from '../contexts/animals';
import { useRacesList } from '../../races/contexts/races';
import { useFarmsList } from '../../farms/contexts/farms';
import OwnersModal from '../forms/owners-modal';
import SectionTabs from '../../navigation/section-tabs';
import { ANIMAL_TABS } from '../../navigation/tabs';

import '../../../common/styles/catalog.css';

const STATUS_LABEL = { 2: 'Activo', 1: 'Inactivo', 0: 'Eliminado' };
const SEX_LABEL = { female: 'Hembra', male: 'Macho' };

// `sex` fija la sección: 'female' → Donadoras · 'male' → Toros · undefined → Animales.
export default function AnimalsOverview({ sex: forcedSex }) {
    const [params, setParams] = useSearchParams();
    const [ownersFor, setOwnersFor] = useState(null); // vaca cuyo modal de copropietarios está abierto
    const urlSearch = params.get('q') || '';
    const urlRace   = params.get('race_id') || '';
    const urlFarm   = params.get('farm_id') || ''; // finca actual del animal (current_farm_id, 0125)
    const urlSex    = params.get('sex') || '';
    const urlStatus = params.get('status') || '';
    const urlReg    = params.get('reg') || ''; // 'with' | 'without' — client-side (not in contract)

    const [search, setSearch] = useState(urlSearch);
    const [debounced, setDebounced] = useState(urlSearch);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        const next = new URLSearchParams(params);
        if (debounced) next.set('q', debounced); else next.delete('q');
        setParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced]);

    const setParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value); else next.delete(key);
        setParams(next, { replace: true });
    };

    const clearFilters = () => {
        setSearch('');
        setParams(new URLSearchParams(), { replace: true });
    };

    const { data: racesData } = useRacesList({});
    const races = racesData?.items ?? [];
    const { data: farmsData } = useFarmsList({ limit: 500, sort: 'name' });
    const farms = farmsData?.items ?? [];

    // Server-side filters (per api-reference/admin-animals.md).
    const filters = useMemo(() => {
        const out = {};
        if (debounced) out.q = debounced;
        if (urlRace)   out.race_id = urlRace;
        if (urlFarm)   out.current_farm_id = urlFarm;
        if (forcedSex) out.sex = forcedSex; else if (urlSex) out.sex = urlSex;
        if (urlStatus) out.status = urlStatus;
        return out;
    }, [debounced, urlRace, urlFarm, urlSex, urlStatus, forcedSex]);

    const { data, isLoading, isFetching, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useAnimalsInfinite(filters);
    const animals = useMemo(() => data?.pages?.flatMap(p => p.items ?? []) ?? [], [data]);

    // 'con/sin registro' has no server param → filter the loaded pages client-side.
    const visible = useMemo(() => {
        if (urlReg === 'with') return animals.filter(a => !!a.register);
        if (urlReg === 'without') return animals.filter(a => !a.register);
        return animals;
    }, [animals, urlReg]);

    const total = data?.pages?.[0]?.pagination?.total ?? animals.length;
    const displayCount = urlReg ? visible.length : total;
    const hasFilters = !!(search || urlRace || urlFarm || (!forcedSex && urlSex) || urlStatus || urlReg);
    const title = forcedSex === 'female' ? 'Donadoras' : forcedSex === 'male' ? 'Toros' : 'Animales';
    const addLabel = forcedSex === 'male' ? 'Nuevo toro' : forcedSex === 'female' ? 'Nueva donadora' : 'Nuevo animal';

    return (
        <div className="cat-overview">
            <SectionTabs tabs={ANIMAL_TABS} />
            <header className="cat-overview__header">
                <div className="cat-overview__heading">
                    <h1 className="cat-overview__title">{title}</h1>
                    <span className="cat-overview__count">{displayCount}</span>
                </div>
                <Gate domain="animals" action="write">
                    <Link to="/admin/animals/new" className="cat-overview__add">
                        <IconPlus />
                        <span>{addLabel}</span>
                    </Link>
                </Gate>
            </header>

            <div className="cat-overview__toolbar">
                <div className="cat-overview__search">
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar por nombre, número o registro…"
                        aria-label="Buscar animales por nombre, número o registro"
                    />
                </div>

                <select className="cat-overview__filter" value={urlRace} onChange={(e) => setParam('race_id', e.target.value)} aria-label="Filtrar por raza">
                    <option value="">Todas las razas</option>
                    {races.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>

                <select className="cat-overview__filter" value={urlFarm} onChange={(e) => setParam('farm_id', e.target.value)} aria-label="Filtrar por finca">
                    <option value="">Todas las fincas</option>
                    {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>

                {!forcedSex && (
                    <select className="cat-overview__filter" value={urlSex} onChange={(e) => setParam('sex', e.target.value)} aria-label="Filtrar por sexo">
                        <option value="">Todos los sexos</option>
                        <option value="female">Hembra</option>
                        <option value="male">Macho</option>
                    </select>
                )}

                <select className="cat-overview__filter" value={urlStatus} onChange={(e) => setParam('status', e.target.value)} aria-label="Filtrar por estado">
                    <option value="">Activos e inactivos</option>
                    <option value="2">Activo</option>
                    <option value="1">Inactivo</option>
                </select>

                <select className="cat-overview__filter" value={urlReg} onChange={(e) => setParam('reg', e.target.value)} aria-label="Filtrar por registro">
                    <option value="">Con y sin registro</option>
                    <option value="with">Con registro</option>
                    <option value="without">Sin registro</option>
                </select>

                {hasFilters && (
                    <button type="button" className="cat-overview__clear" onClick={clearFilters}>Limpiar</button>
                )}
            </div>

            {error && <div className="cat-overview__error">{error.message || 'No se pudieron cargar los animales'}</div>}

            {isLoading ? (
                <div className="cat-overview__state">Cargando animales…</div>
            ) : !visible.length ? (
                <div className="cat-overview__state">
                    <p className="cat-overview__state-text">
                        {hasFilters ? 'Ningún animal coincide con el filtro.' : 'Aún no hay animales.'}
                    </p>
                </div>
            ) : (
                <div className={`cat-overview__card ${isFetching ? 'cat-overview__card--busy' : ''}`}>
                    <table className="cat-table cat-table--compact">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Número</th>
                                <th>Registro</th>
                                <th>Raza</th>
                                <th>Sexo</th>
                                <th>Estado</th>
                                <th className="cat-table__actions-col" aria-label="Acciones" />
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((a) => (
                                <tr key={a.id} className="cat-table__row">
                                    <td>
                                        <Link to={`/admin/animals/${a.id}`} className="cat-table__name">
                                            {a.name || a.number || `#${a.id}`}
                                        </Link>
                                    </td>
                                    <td>{a.number || <span className="cat-table__empty">—</span>}</td>
                                    <td>{a.register || <span className="cat-table__empty">—</span>}</td>
                                    <td>{a.race?.name || <span className="cat-table__empty">—</span>}</td>
                                    <td>{SEX_LABEL[a.sex] || <span className="cat-table__empty">—</span>}</td>
                                    <td><span className={`cat-badge cat-badge--${a.status}`}>{STATUS_LABEL[a.status] || '—'}</span></td>
                                    <td className="cat-table__actions">
                                        <Gate domain="kpis" action="read">
                                            <Link to={`/admin/animals/${a.id}/stats`} className="cat-table__edit" aria-label={`Estadísticas de ${a.name || a.number || a.id}`} title="Estadísticas">
                                                <IconChart />
                                            </Link>
                                        </Gate>
                                        <Gate domain="billing" action="read">
                                            <Link to={`/admin/animals/${a.id}/liquidation`} className="cat-table__edit" aria-label={`Liquidar ${a.name || a.number || a.id}`} title="Liquidar">
                                                <IconReceipt />
                                            </Link>
                                        </Gate>
                                        {a.sex === 'female' && (
                                            <Gate domain="animals" action="write">
                                                <button type="button" className="cat-table__edit" onClick={() => setOwnersFor(a)} aria-label={`Copropietarios de ${a.name || a.number || a.id}`} title="Administrar copropietarios">
                                                    <IconUsers />
                                                </button>
                                            </Gate>
                                        )}
                                        <Gate domain="animals" action="write">
                                            <Link to={`/admin/animals/${a.id}/edit`} className="cat-table__edit" aria-label={`Editar ${a.name || a.number || a.id}`} title="Editar">
                                                <IconEdit />
                                            </Link>
                                        </Gate>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!isLoading && !error && visible.length > 0 && (
                <LoadMore
                    shown={visible.length}
                    total={total}
                    hasMore={!!hasNextPage}
                    loading={isFetchingNextPage}
                    onMore={() => fetchNextPage()}
                    noun={title.toLowerCase()}
                />
            )}

            {ownersFor && <OwnersModal animal={ownersFor} onClose={() => setOwnersFor(null)} />}
        </div>
    );
}
